<template>
  <div class="rich-wrap-input-2233">
    <Toolbar
      style="border-bottom: 1px solid #ccc"
      :editor="editorRef"
      :defaultConfig="toolbarConfig"
      :mode="mode"
    />
    <Editor
      :style="{ height: height }"
      :model-value="valueHtml"
      :defaultConfig="editorConfig"
      :mode="mode"
      @onCreated="handleCreated"
      @update:modelValue="updateVal"
    />

    <el-dialog
      class="kityFormula-dialog"
      v-model="kityFormulaDiaVis"
      title="插入KityFormula公式"
      width="820"
      append-to-body
      destroy-on-close
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <div class="kityFormula-content">
        <iframe ref="kityFormulaIframeRef" :src="kityFormulaIframeUrl" />
      </div>
      <div class="kityFormula-footer">
        <el-button type="primary" @click="kityFormulaConfirm">确定</el-button>
        <el-button @click="kityFormulaDiaVis = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 预览云点播视频 -->
    <tc-player-dialog v-model:visible="playerVis" :video-id="playerId" />
    <!-- 视频上传 -->
    <choice-file-dialog
      v-model:visible="choiceFileVis"
      accept="视频"
      :acceptType="['MP4', 'Excel']"
      :limit="1"
      @confirm="addFile"
    />
  </div>
</template>

<script setup>
import { Editor, Toolbar } from "@wangeditor/editor-for-vue";
import { DomEditor, Boot } from "@wangeditor/editor";
import "@wangeditor/editor/dist/css/style.css"; // 引入 css
import { getToken } from "@/utils/auth";
import { ElMessage, ElMessageBox, useFormItem } from "element-plus";
import { Base64 } from "js-base64";
import tcPlayerDialog from "@/views/education/courseContentLibrary/cmp/tcPlayerDialog.vue";
import ChoiceFileDialog from "@/views/education/preparationCourse/dja/cmp/choiceFileDialog.vue";
import moduleVideoAttachment from "./RichTextInputModule/videoAttachment/index.js";
import { getVideoPlayToken } from "@/api/education/courseContentLibrary";
import useUserStore from "@/store/modules/user";

/*
 * wangeditor 富文本编辑器，封装。已实现双向绑定。
 */
const { formItem } = useFormItem();
const props = defineProps({
  modelValue: {
    // 直接使用v-model，此组件已实现双向绑定
    type: String,
    default: "",
  },
  height: {
    // 输入区域高度
    type: String,
    default: "300px", // 高度建议不低于300px
  },
  readOnly: {
    // 是否只读
    type: Boolean,
    default: false,
  },
  useBase64: {
    // 传输过程中采用base64加解码，防止存入数据库异常
    type: Boolean,
    default: false,
  },
  excludeKeys: {
    default: () => [
      "videoAttachment",
      "group-video",
      "fullScreen",
      "blockquote",
      "fontFamily",
      "emotion",
      "codeBlock",
    ], // 排除某些无用功能
  },
  toolbarKeys: {
    default: () => [],
  },
  log: {
    default: false,
  },
  placeholder: {
    type: String,
    default: "请输入内容...",
  },
});

const toolbarConfig = computed(() => {
  const { excludeKeys, toolbarKeys } = props;

  const toolbarConfig = {
    excludeKeys, // 排除某些无用功能
    insertKeys: {
      index: 0,
      keys: ["kityFormula", "videoAttachment", "uploadAttachment"], // show menu in toolbar
    },
  };

  if (toolbarKeys instanceof Array && toolbarKeys.length > 0) {
    Object.assign(toolbarConfig, {
      toolbarKeys,
    });
  }

  return toolbarConfig;
});

const emit = defineEmits(["update:modelValue"]);
const isValidateChange = ref(false);
function updateVal(val) {
  if (val == "<p><br></p>") {
    // 删空了
    emit("update:modelValue", "");
    return;
  }

  if (props.useBase64) {
    emit("update:modelValue", Base64.encode(val));
  } else {
    emit("update:modelValue", val);
  }

  if (isValidateChange.value) {
    formItem?.validate?.("change");
  }
  isValidateChange.value = true;
}

const valueHtml = ref("");
watch(
  () => props.modelValue,
  (newVal) => {
    if (!newVal) {
      valueHtml.value = "";
      return;
    }

    nextTick(() => {
      valueHtml.value = newValFormat(newVal);
    });
  },
  { immediate: true }
);

function newValFormat(newVal) {
  try {
    const decoded = Base64.decode(newVal);

    if (Base64.encode(decoded) === newVal) {
      newVal = decoded;
    }
  } catch (e) {}

  return newVal.replace(/^<br\s*\/?>/i, "");
}

// 数学公式
const kityFormulaDiaVis = ref(false);
const kityFormulaEditorCurrent = ref(null);
class KityFormula {
  constructor() {
    this.title = "插入公式";
    this.iconSvg =
      '<svg t="1749452213431" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4326" width="200" height="200"><path d="M358.4 298.666667a85.333333 85.333333 0 0 1 85.333333-85.333334h85.333334a42.666667 42.666667 0 0 0 42.666666-42.666666 42.666667 42.666667 0 0 0-42.666666-42.666667h-85.333334a170.666667 170.666667 0 0 0-170.666666 170.666667v597.333333a42.666667 42.666667 0 0 0 42.666666 42.666667 42.666667 42.666667 0 0 0 42.666667-42.666667z" fill="#666666" p-id="4327"></path><path d="M145.066667 426.666667m42.666666 0l298.666667 0q42.666667 0 42.666667 42.666666l0 0q0 42.666667-42.666667 42.666667l-298.666667 0q-42.666667 0-42.666666-42.666667l0 0q0-42.666667 42.666666-42.666666Z" fill="#666666" p-id="4328"></path><path d="M865.706667 426.666667a42.666667 42.666667 0 0 0-37.12 21.333333l-85.333334 149.333333-85.333333-149.333333a42.666667 42.666667 0 0 0-37.12-21.333333 42.666667 42.666667 0 0 0-36.693333 64l110.933333 192-110.933333 192a42.666667 42.666667 0 0 0 36.693333 64 42.666667 42.666667 0 0 0 37.12-21.333334l85.333333-149.333333 85.333334 149.333333a42.666667 42.666667 0 0 0 37.12 21.333334 42.666667 42.666667 0 0 0 36.693333-64l-110.933333-192 110.933333-192a42.666667 42.666667 0 0 0-36.693333-64z" fill="#666666" p-id="4329"></path></svg>';
    this.tag = "button";
  }

  // 获取菜单执行时的 value，返回空字符串或 false
  getValue(editor) {
    return ""; // 默认返回空
  }

  // 菜单是否需要激活（如选中加粗文本，“加粗”菜单会激活），用不到则返回 false
  isActive(editor) {
    return false; // 默认不激活
  }

  // 菜单是否需要禁用（如选中 H1，“引用”菜单被禁用），用不到则返回 false
  isDisabled(editor) {
    return false; // 默认不禁用
  }

  // 点击菜单时触发的函数
  exec(editor, value) {
    if (this.isDisabled(editor)) return;
    editor.emit("kityFormula:menu:click");
  }
}

const kityFormulaIframeOrigin = `${document.location.protocol}//static.rhkj.com`;
const kityFormulaIframeUrl = `${kityFormulaIframeOrigin}/renhe_fe/other/kityformula-plugins/kityFormulaLayer.html?v=1`;
const kityFormulaIframeRef = ref();
const msId = generateId();
const kityFormulaConfirm = () => {
  try {
    kityFormulaIframeRef.value.contentWindow.postMessage(
      {
        type: "getImageURL",
        msId,
      },
      "*" // 更严格的使用变量 kityFormulaIframeOrigin
    );
  } catch (e) {
    console.log(e);
  }
};

window.addEventListener("message", function (event) {
  if (event.data.msId != msId) {
    return;
  }

  if (event.data.type === "getImageURL") {
    const node = {
      type: "image",
      src: event.data.data,
      children: [{ text: "" }],
    };
    kityFormulaEditorCurrent.value.insertNode(node);
    kityFormulaDiaVis.value = false;
  }
});

// 视频插入
const choiceFileVis = ref(false);
const playerVis = ref(false);
const playerId = ref("");
const videoAttachmentEditorCurrent = ref(null);
class VideoAttachment {
  constructor() {
    this.title = "插入素材";
    this.iconSvg =
      '<svg data-v-58697b5c="" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><path fill="currentColor" d="M832 384H576V128H192v768h640zm-26.496-64L640 154.496V320zM160 64h480l256 256v608a32 32 0 0 1-32 32H160a32 32 0 0 1-32-32V96a32 32 0 0 1 32-32m320 512V448h64v128h128v64H544v128h-64V640H352v-64z"></path></svg>';
    this.tag = "button";
  }

  // 获取菜单执行时的 value，返回空字符串或 false
  getValue(editor) {
    return ""; // 默认返回空
  }

  // 菜单是否需要激活（如选中加粗文本，“加粗”菜单会激活），用不到则返回 false
  isActive(editor) {
    return false; // 默认不激活
  }

  // 菜单是否需要禁用（如选中 H1，“引用”菜单被禁用），用不到则返回 false
  isDisabled(editor) {
    return false; // 默认不禁用
  }

  // 点击菜单时触发的函数
  exec(editor, value) {
    if (this.isDisabled(editor)) return;
    editor.emit("videoAttachment:menu:click");
  }
}

const { userInfo } = useUserStore();
const addFile = async ([data]) => {
  console.log("选择文件", data);
  const { fileType } = data;
  const type = data.type.toLowerCase();
  let node;

  if (type == "mp4") {
    const { storageId: videoUrl, name: videoName } = data;

    const res = await getVideoPlayToken(`${userInfo.userId}/${videoUrl}`);

    const videoCover =
      res.data?.basicInfo?.coverUrl ||
      "https://static.rhkj.com/renhe_fe/other/crm/1749631669003_11112.png";

    node = {
      type: "videoAttachment",
      videoUrl,
      videoName,
      videoCover,
      children: [{ text: "" }],
    };
  }

  if (fileType == 1) {
    const { storageId, name, fileExtension, fileType } = data;

    node = {
      type: "fileAttachment",
      fileStorageId: storageId,
      fileName: name,
      fileExtension,
      fileType,
      children: [{ text: "" }],
    };
  }

  if (node) {
    videoAttachmentEditorCurrent.value.insertNode(node);
  }
};

const menuConfKityFormula = {
  key: "kityFormula",
  factory: () => new KityFormula(),
};

const menuConfVideoAttachment = {
  key: "videoAttachment",
  factory: () => new VideoAttachment(),
};

if (!Boot.$wangEditorRegisterMenu) {
  Boot.registerModule(moduleVideoAttachment);
  Boot.registerMenu(menuConfKityFormula);
  Boot.registerMenu(menuConfVideoAttachment);
  Boot.$wangEditorRegisterMenu = true;
}

const action = ref(import.meta.env.VITE_APP_BASE_API + "/file/upload"); // 上传的图片服务器地址
const maxFileSize = 10; // 最大上传图标大小单位M
const editorConfig = {
  placeholder: props.placeholder,
  scroll: true,
  autoFocus: false,
  readOnly: props.readOnly,
  MENU_CONF: {
    ["uploadImage"]: {
      // 上传图片配置
      server: action.value,
      fieldName: "file", // form-data fieldName
      maxFileSize: maxFileSize * 1024 * 1024, // 单个文件的最大体积限制
      maxNumberOfFiles: 10, // 最多可上传几个文件，默认为 100
      allowedFileTypes: ["image/*"], // 选择文件时的类型限制，默认为 ['image/*']

      // 自定义上传参数，例如传递验证的 token 等。参数会被添加到 formData 中，一起上传到服务端。
      meta: {},
      headers: {
        // 自定义增加 http  header
        Authorization: "Bearer " + getToken(),
      },
      timeout: 10 * 1000, // 超时时间，默认为 10 秒
      customInsert(res, insertFn) {
        if (res.code === 200) {
          insertFn(res.data.url); // 调用插入图片方法
        } else {
          ElMessage.error("图片上传失败:" + res.msg);
        }
      },
      onError(file, err) {
        if (String(err).includes("exceeds maximum allowed size of")) {
          ElMessage.error(`上传的图片大小不能超过${maxFileSize}M`);
          return;
        }

        ElMessage.error(String(err));
      },
    },
    ["uploadAttachment"]: {
      // 上传文件配置
      server: action.value,
      fieldName: "file", // form-data fieldName
      maxFileSize: 50 * 1024 * 1024, // 单个文件的最大体积限制 50m
      maxNumberOfFiles: 10, // 最多可上传几个文件，默认为 100
      allowedFileTypes: [
        // Word
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        // Excel
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        // PowerPoint
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        // 也可以添加 PDF 等常见办公文档
        'application/pdf',
        'text/plain'
      ],

      // 自定义上传参数，例如传递验证的 token 等。参数会被添加到 formData 中，一起上传到服务端。
      meta: {},
      headers: {
        // 自定义增加 http  header
        Authorization: "Bearer " + getToken(),
      },
      timeout: 10 * 1000, // 超时时间，默认为 10 秒
      customInsert(res, file, insertFn) {
        console.log(res)
        if (res.code === 200) {
          insertFn(`${res.data.name}`, res.data.url)
        } else {
          ElMessage.error("文件上传失败:" + res.msg);
        }
      },
    },
  },
};
const mode = ref("default");

// 编辑器实例，必须用 shallowRef
const editorRef = shallowRef();
const handleCreated = (editor) => {
  editorRef.value = editor; // 记录 editor 实例，重要！

  editor.on("kityFormula:menu:click", () => {
    kityFormulaEditorCurrent.value = editor;
    kityFormulaDiaVis.value = true;
  });

  editor.on("videoAttachment:menu:click", () => {
    videoAttachmentEditorCurrent.value = editor;
    choiceFileVis.value = true;
  });

  editor.on("videoAttachment:el:click", (data) => {
    playerId.value = data.videoUrl;
    playerVis.value = true;
  });

  editor.on("fileAttachment:el:click", (data) => {
    const baseUrl = `${location.origin}${import.meta.env.VITE_APP_BASE_API}`;
    const path = `${baseUrl}/storage/api/files/download/${
      data.fileStorageId
    }?name=${data.fileStorageId}.${data.fileExtension?.toLocaleLowerCase()}`;
    window.open(
      `/onlinePreviewFile?fileUrl=${encodeURIComponent(path)}`,
      "_blank"
    );
  });

  nextTick(() => {
    if (props.log) {
      const toolbar = DomEditor.getToolbar(editor);
      const curToolbarConfig = toolbar.getConfig();
      console.log(JSON.stringify(curToolbarConfig.toolbarKeys)); // 当前菜单排序和分组
    }
  });
};

watch(
  () => props.readOnly,
  (val) => {
    // 支持readOnly属性响应式
    if (!val) {
      editorRef.value.enable();
    } else {
      editorRef.value.disable();
    }
  }
);

// 组件销毁时，也及时销毁编辑器
onBeforeUnmount(() => {
  const editor = editorRef.value;
  if (editor == null) return;
  editor.destroy();
});

const getEditor = () => {
  return editorRef.value;
};

defineExpose({
  getEditor,
});

function generateId(length = 20) {
  return Math.random()
    .toString(36)
    .substring(2, length + 2);
}
</script>

<style lang="scss">
.rich-wrap-input-2233 {
  width: 100%;
  border: 1px solid #ccc;

  //支持标题、粗体、斜体样式
  h1 {
    font-size: 32px;
    font-weight: bold;
    span {
      font-weight: bold;
    }
  }
  h2 {
    font-size: 24px;
    font-weight: bold;
    span {
      font-weight: bold;
    }
  }
  h3 {
    font-size: 18px;
    font-weight: bold;
    span {
      font-weight: bold;
    }
  }
  h4 {
    font-size: 16px;
    font-weight: bold;
    span {
      font-weight: bold;
    }
  }
  h5 {
    font-size: 13px;
    font-weight: bold;
    span {
      font-weight: bold;
    }
  }
  strong {
    font-weight: bold;
    span {
      font-weight: bold;
    }
  }
  .italic {
    font-style: italic;
    span {
      font-style: italic;
    }
  }
  em {
    font-style: italic;
    span {
      font-style: italic;
    }
  }
}

.kityFormula-dialog {
  .el-dialog__body {
    padding: 0 !important;

    .kityFormula-content {
      display: flex;
      justify-content: center;

      iframe {
        width: 800px;
        height: 450px;
      }
    }

    .kityFormula-footer {
      display: flex;
      justify-content: center;
      padding: 20px 0;
    }
  }
}
</style>
